import { Area } from '../../types';
import { convertPositionToAddress } from '../../utils/address/singleAddress';
import { doAreasIntersect, isCellInsideArea } from '../../utils/area/area';
import {
  getFreezeBarHandleSize,
  getSizeForViewportIndex,
} from '../../utils/viewport/viewportUtils';
import { getViewportStartEnd } from '../../utils/views/virtualViewport';
import {
  GetHiddenRanges,
  GetHiddenRangesEmpty,
  getHiddenRanges,
} from './cellHidden';
import { getCellVisibleData } from './cellView';
import { getMergedCellDimensions } from './cellViewHelpers';
import {
  ColumnCellView,
  RowCellsProps,
  RowCellView,
  RowsViewConfigs,
} from './cellViewTypes';

type Configs = Partial<RowsViewConfigs>;
type CellViewCallback = (p: RowCellView) => JSX.Element;
type ColumnView = Partial<ColumnCellView>;

export function getRowsCellView(
  props: RowCellsProps,
  getCell: CellViewCallback,
  configs: Configs = {},
  columnView: ColumnView = {}
) {
  const Cells: JSX.Element[] = [];

  const getCells = getRowCells(props, getCell, configs, columnView, Cells);

  getCells();

  const isFrozen = props.freezedRows.frozen;
  if (isFrozen) getCells(true);

  return Cells;
}

// #region getRowCells
const getRowCells = (
  props: RowCellsProps,
  getCell: CellViewCallback,
  configs: Configs,
  columnView: ColumnView,
  Cells: JSX.Element[]
) => {
  // #region variables
  const params = getViewParams(props);

  const {
    zoom,
    defaultRowHeight: defaultHeight,
    hiddenRows,
    maxRowIndex,
    freezedRows,
    filterResults,
  } = props;

  const {
    mergedCellsInViewport: viewportMerges,
    renderedMergedCells,
    ...column
  } = columnView;

  const { ci, width, x } = column;
  const getMergedCell = handleMergedCell(props, ci, x, renderedMergedCells);
  const getCellData = getCellVisibleData(props, ci, width);
  const { checkHiddenRows } = configs;
  // #endregion

  // #region Get Cell
  return (frozenRow = false) => {
    const key = frozenRow ? 'frozen' : '';
    const sizes = params[`${key}Sizes`];
    const indexes = params[`${key}Indexes`];
    let y = params[`${key}Y`];

    const getMergedArea = getMergedCellsInColumn(indexes, ci, viewportMerges);
    let getHiddens: GetHiddenRanges | GetHiddenRangesEmpty = () => undefined;
    if (checkHiddenRows) {
      getHiddens = getHiddenRanges(
        indexes,
        hiddenRows,
        maxRowIndex,
        freezedRows,
        filterResults
      );
    }

    // #region Iterate Viewport
    const maxIndexes = indexes.length;
    for (let pos = 0; pos < maxIndexes; pos++) {
      const ri = indexes[pos];
      const height = getSizeForViewportIndex(sizes, pos, defaultHeight, zoom);

      let rowParams: RowCellView | undefined = {
        ri,
        height,
        y,
        frozenRow,
        hiddenRows: getHiddens(pos, ri),
        ...column,
      };

      const merged = getMergedArea(ri);
      if (merged) rowParams = getMergedCell(merged, rowParams);

      if (rowParams) {
        rowParams = {
          ...rowParams,
          ...getCellData(ri),
        };
        Cells.push(getCell(rowParams));
      }

      y += height;
    }
    // #endregion
  };
  // #endregion
};
// #endregion

// #region handle merged cell
const handleMergedCell = (
  props: RowCellsProps,
  currentCi: number | undefined,
  currentX: number | undefined,
  renderedMergedCells?: Set<string>
) => {
  if (
    currentCi === undefined ||
    currentX === undefined ||
    !renderedMergedCells
  ) {
    return () => undefined;
  }

  return (area: Area, rowProps: RowCellView) => {
    const { ri: currentRi, y: currentY } = rowProps;
    // const { ci: currentCi = -1, x: currentX = 0 } = column;
    const position = {
      ci: area.start.ci,
      ri: area.start.ri,
    };
    const address = convertPositionToAddress(position);
    if (renderedMergedCells?.has(address)) return undefined;

    const dimensions = getMergedCellDimensions(
      props,
      area,
      currentRi,
      currentY,
      currentCi,
      currentX
    );

    rowProps = {
      ...rowProps,
      ...position,
      ...dimensions,
    };
    renderedMergedCells?.add(address);

    return rowProps;
  };
};
// #endregion

// #region get view params
const getViewParams = (props: RowCellsProps) => {
  const {
    colHeaderHeight,
    rowViewport,
    colFilterHeight,
    freezedRows,
    freezedRowsSum,
    zoom,
  } = props;

  const { frozenIndexes, frozenSizes, nonFrozenIndexes, nonFrozenSizes } =
    rowViewport;

  let frozenY = colHeaderHeight + colFilterHeight; // groupBarHeight +

  const Y = freezedRows.frozen
    ? frozenY + freezedRowsSum + getFreezeBarHandleSize(freezedRows, zoom)
    : frozenY;

  return {
    frozenIndexes,
    frozenSizes,
    frozenY,
    Indexes: nonFrozenIndexes,
    Sizes: nonFrozenSizes,
    Y,
  };
};
// #endregion

// #region get merged cells in column
const getMergedCellsInColumn = (
  indexes: number[],
  ci?: number,
  mergedCellsInViewport?: Area[]
): ((ri: number) => Area | undefined) => {
  if (ci === undefined) return () => undefined;

  const { start: rs, end: re } = getViewportStartEnd(indexes);

  const merges = mergedCellsInViewport?.filter((area) =>
    doAreasIntersect(area, {
      start: { ...area.start, ri: rs },
      end: { ...area.end, ri: re },
    })
  );

  if (merges)
    return (ri: number) => {
      const position = {
        ci,
        ri,
      };

      return merges.find((area) => isCellInsideArea(position, area));
    };

  return () => undefined;
};
// #endregion
